/**
 * Client-side error reporting.
 *
 * A production React crash arrives as "Minified React error #310" and a blank
 * screen. That string is useless on its own: it names the invariant but not the
 * rule that was broken, not the route, and not the state the learner was in.
 * This module turns each crash into one queryable `ops_events` row that carries
 *
 *   - the decoded invariant ("Rendered more hooks than during the previous
 *     render" instead of "#310")
 *   - the route, query string and band the learner was on
 *   - the React component stack, trimmed to the frames that matter
 *   - build commit, viewport and whether the build was minified
 *
 * It reports to the same pipeline the ops dashboard already reads, so a crash
 * shows up next to the failed writes and RLS denials rather than in a separate
 * place nobody opens. Reporting is fire-and-forget and never throws: an error
 * reporter that can itself crash the app is worse than no reporter.
 */
import { recordOps } from "@/lib/observability";

/**
 * The React invariants worth naming, because these are the ones that present as
 * a blank screen with no other clue. Anything not listed still reports, with
 * the decoder URL attached so the number can be resolved by hand.
 */
const REACT_INVARIANTS: Record<string, string> = {
  "185": "setState called on an unmounted component (infinite update loop)",
  "300": "Rendered fewer hooks than expected — a hook ran after an early return",
  "301": "Too many re-renders — a state update is running on every render",
  "310": "Rendered more hooks than during the previous render — a hook is running after an early return or inside a condition",
  "321": "Invalid hook call — a hook was called outside a function component",
  "418": "Hydration failed — server and client markup differ",
  "423": "Recoverable hydration error — the tree was re-rendered on the client",
  "425": "Text content did not match between server and client",
};

const DECODER = "https://react.dev/errors";

/** Pull the invariant number out of a minified React message, if present. */
export function reactInvariantNumber(message: string): string | null {
  const m = /Minified React error #(\d+)/.exec(message)
    ?? /invariant=(\d+)/.exec(message)
    ?? /react\.dev\/errors\/(\d+)/.exec(message);
  return m ? m[1] : null;
}

/**
 * Expand a minified React message into something a human can act on.
 *
 * Returns the original message untouched when it is already readable (dev
 * builds, or a plain application error).
 */
export function decodeReactError(message: string): {
  message: string;
  invariant: string | null;
  hint: string | null;
  docs: string | null;
} {
  const num = reactInvariantNumber(message);
  if (!num) return { message, invariant: null, hint: null, docs: null };
  const hint = REACT_INVARIANTS[num] ?? null;
  return {
    message: hint ? `React #${num}: ${hint}` : `React #${num} (undecoded)`,
    invariant: num,
    hint,
    docs: `${DECODER}/${num}`,
  };
}

/** Keep stacks small enough to read, and small enough to store. */
function trim(stack: string | null | undefined, lines: number, chars: number): string | null {
  if (!stack) return null;
  return stack.split("\n").slice(0, lines).join("\n").slice(0, chars);
}

/** Where the learner was, in the terms a debugger asks about first. */
function routeContext(): Record<string, unknown> {
  if (typeof window === "undefined") return {};
  const url = new URL(window.location.href);
  const query: Record<string, string> = {};
  url.searchParams.forEach((v, k) => { query[k] = v.slice(0, 60); });
  return {
    route: url.pathname,
    query,
    // `band` decides which result surface renders, so it is the single most
    // useful input to know when a result page crashes.
    band: url.searchParams.get("band") ?? null,
    referrer: document.referrer ? new URL(document.referrer, url.origin).pathname : null,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  };
}

function buildContext(): Record<string, unknown> {
  return {
    commit: typeof __APP_COMMIT__ === "string" ? __APP_COMMIT__.slice(0, 12) : "unknown",
    // Minified builds are the ones that hide the real message; recording this
    // tells us whether a bare "#310" was expected or is a build-config bug.
    minified: !import.meta.env.DEV,
    mode: import.meta.env.MODE,
    ua: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 180) : null,
  };
}

export type ClientErrorReport = {
  /** Where the failure was caught: an error boundary, a global handler, a promise. */
  source: "react_boundary" | "window_error" | "unhandled_rejection" | "manual";
  error: unknown;
  /** React's component stack, when a boundary caught it. */
  componentStack?: string | null;
  /** Which component tree the boundary was guarding. */
  boundary?: string | null;
  /** Anything the caller knows that the URL does not. */
  context?: Record<string, unknown>;
};

/** De-duplicate: a crash loop must not become a thousand identical rows. */
const seen = new Map<string, number>();
const DEDUPE_MS = 30_000;

function isDuplicate(key: string): boolean {
  const now = Date.now();
  for (const [k, t] of seen) if (now - t > DEDUPE_MS) seen.delete(k);
  const prev = seen.get(key);
  seen.set(key, now);
  return prev !== undefined && now - prev < DEDUPE_MS;
}

/**
 * Report one client-side error. Safe to call from anywhere, including inside a
 * failing render path.
 */
export function reportClientError(report: ClientErrorReport): void {
  try {
    const err = report.error;
    const raw = err instanceof Error ? err.message : String(err);
    const decoded = decodeReactError(raw);
    const key = `${report.source}:${decoded.invariant ?? raw.slice(0, 80)}:${report.boundary ?? ""}`;
    if (isDuplicate(key)) return;

    recordOps({
      area: "other",
      name: "client_error",
      ok: false,
      error_code: decoded.invariant ? `react_${decoded.invariant}` : (err instanceof Error ? err.name : "error"),
      error_message: decoded.message.slice(0, 300),
      props: {
        source: report.source,
        boundary: report.boundary ?? null,
        raw_message: raw.slice(0, 300),
        invariant: decoded.invariant,
        invariant_hint: decoded.hint,
        docs: decoded.docs,
        stack: trim(err instanceof Error ? err.stack : null, 16, 2000),
        component_stack: trim(report.componentStack, 20, 2000),
        ...routeContext(),
        ...buildContext(),
        ...(report.context ?? {}),
      },
    });

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error(`[client-error] ${decoded.message}`, { report, decoded });
    }
  } catch {
    /* a reporter must never be the thing that breaks the page */
  }
}

let installed = false;

/**
 * Catch what error boundaries cannot: errors thrown outside React's render
 * cycle, and rejected promises nobody awaited.
 */
export function installGlobalErrorReporting(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (e: ErrorEvent) => {
    // Resource load failures (a missing image) surface here too and are noise.
    if (!e.error && !e.message) return;
    reportClientError({
      source: "window_error",
      error: e.error ?? e.message,
      context: { file: e.filename ?? null, line: e.lineno ?? null, col: e.colno ?? null },
    });
  });

  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    reportClientError({ source: "unhandled_rejection", error: e.reason });
  });
}
